<template>
  <!--
    AB#21297 — `compact` renders the trigger as an icon button: the funnel, a
    tooltip that says "Filter", and a small count of active filters in place of
    the filled-green state. Clearing stays one click, on the badge itself.
    Every other caller keeps the labelled trigger.
  -->
  <Dropdown
    ref="panel"
    @open="onPanelOpen"
    :trigger-style="
      props.compact
        ? 'dash-icon-btn relative'
        : `${selectedQuickFilters.length || selectedUrgentFilters.length || customFilter.length ? '!bg-[#38aa90bb] text-white !border-[#38aa90bb] !hover:bg-[#38aa90bb]' : ''} text-sm px-2 py-2 h-max font-medium text-ink border-[1px] border-line bg-surface rounded-[10px] w-max  hover:bg-surface-2`
    "
    :trigger-aria-label="props.compact ? 'Filter' : undefined"
    :close-on-outside-click="true"
  >
    <template #trigger>
      <span v-if="props.compact" class="contents" title="Filter">
        <FunnelIcon class="size-5" aria-hidden="true" />
        <span
          v-if="activeFilterCount"
          class="dash-icon-badge"
          role="button"
          tabindex="0"
          :title="`Clear ${activeFilterCount} filter${activeFilterCount === 1 ? '' : 's'}`"
          :aria-label="`Clear ${activeFilterCount} filter${activeFilterCount === 1 ? '' : 's'}`"
          @click.stop="clearFilters"
          @keydown.enter.stop.prevent="clearFilters"
        >
          {{ activeFilterCount }}
        </span>
      </span>
      <button v-else class="flex items-center justify-between gap-3">
        <span class="flex gap-1 items-center justify-between">
          <FunnelIcon class="size-5" />
          Filter
        </span>
        <span
          class="bg-transparent"
          v-if="selectedQuickFilters.length || selectedUrgentFilters.length || customFilter.length"
          @click.stop="clearFilters"
        >
          <XMarkIcon class="size-4" />
        </span>
      </button>
    </template>
    <template #contents>
      <!--
        AB#21490 — the panel. Four groups of chips with live counts, an
        Applied row, the custom filters folded under "More filters", and one
        button that says what pressing it shows. The state underneath (quick
        keys, flags, custom filters, localStorage, the updateFilter emit) is
        the same as before; only the panel changed.
      -->
      <div class="fp" data-test="filter-panel">
        <header class="fp-head">
          <h3 class="fp-title">Filters</h3>
          <span class="fp-count" data-test="fp-count">
            <template v-if="counts.total !== null">
              {{ fmt(counts.total) }} of {{ fmt(counts.base ?? counts.total) }} orders
            </template>
            <template v-else-if="countsLoading">counting…</template>
          </span>
          <button
            v-if="activeFilterCount"
            type="button"
            class="fp-clear"
            data-test="fp-clear-all"
            @click.stop="clearFilters"
          >
            Clear all
          </button>
        </header>

        <div v-if="appliedChips.length" class="fp-applied" data-test="fp-applied">
          <span class="fp-applied-lbl">Applied</span>
          <button
            v-for="chip in appliedChips"
            :key="chip.id"
            type="button"
            class="fp-achip"
            :class="`fp-achip--${chip.tone}`"
            :title="`Remove ${chip.label}`"
            :data-test="`fp-applied-${chip.id}`"
            @click.stop="chip.remove()"
          >
            <span v-if="chip.dot" class="fp-dot" :class="`fp-dot--${chip.dot}`"></span>
            {{ chip.label }}
            <XMarkIcon class="fp-achip-x" aria-hidden="true" />
          </button>
        </div>

        <div class="fp-body">
          <section v-for="group in groups" :key="group.title" class="fp-group" :data-test="`fp-group-${group.id}`">
            <h4 class="fp-group-t">{{ group.title }}</h4>
            <div class="fp-chips">
              <button
                v-for="chip in group.chips"
                :key="chip.value"
                type="button"
                class="fp-chip"
                :class="{
                  'fp-chip--on': chip.selected,
                  'fp-chip--zero': chip.count === 0 && !chip.selected,
                  [`fp-chip--${chip.dot}`]: !!chip.dot,
                }"
                :aria-pressed="chip.selected"
                :data-test="`fp-chip-${chip.value}`"
                @click.stop="chip.toggle()"
              >
                <CheckIcon v-if="chip.selected" class="fp-chip-check" aria-hidden="true" />
                <span v-else-if="chip.dot" class="fp-dot" :class="`fp-dot--${chip.dot}`"></span>
                <span class="fp-chip-lbl">{{ chip.label }}</span>
                <span v-if="chip.count !== null" class="fp-chip-n" :data-test="`fp-n-${chip.value}`">{{ fmt(chip.count) }}</span>
              </button>
            </div>
          </section>

          <section v-if="customFilter.length > 0 || moreOpen" class="fp-group" data-test="fp-more">
            <h4 class="fp-group-t">More filters</h4>
            <div class="flex gap-2 items-start w-full flex-col">
              <template v-for="(filter, filterIndex) in customFilter" :key="filterIndex">
                <div class="flex gap-2 items-center w-full">
                  <Select
                    :options="
                      computedFilterList.filter(
                        (opt) => !selectedAllFilter.some((f) => f.key === opt.key)
                      )
                    "
                    track-by="key"
                    label="label"
                    class="min-w-[150px]"
                    v-model="selectedAllFilter[filterIndex]"
                    append-to-body
                    @option:selected="
                      (option: CustomFilter) => {
                        const resetFilter = { ...option };
                        if (resetFilter.type === 'multi-select') {
                          resetFilter.value = [];
                        } else if (resetFilter.type === 'autocomplete') {
                          resetFilter.value = [];
                        } else if (resetFilter.type === 'date-range') {
                          resetFilter.value = { from: '', to: '' };
                        } else {
                          resetFilter.value = '';
                        }
                        customFilter[filterIndex] = JSON.parse(JSON.stringify(resetFilter));
                        selectedAllFilter[filterIndex] = JSON.parse(JSON.stringify(resetFilter));
                      }
                    "
                    :clearable="false"
                    style="width: fit-content"
                  />
                  <template v-if="!['date', 'date-range'].includes(filter.type)">
                    <Select
                      :options="[
                        { label: 'is', value: 'equal' },
                        { label: 'is not', value: 'not_equal' }
                      ]"
                      track-by="value"
                      label="label"
                      v-model="filter.comparison"
                      :reduce="(option) => option.value"
                      :clearable="false"
                      class="min-w-[100px] max-w-[100px]"
                      append-to-body
                    />
                  </template>
                  <template v-if="filter.type === 'multi-select'">
                    <Select
                      :options="filter.options"
                      track-by="key"
                      label="label"
                      append-to-body
                      v-model="filter.value"
                      class="flex-1"
                      multiple
                    />
                  </template>
                  <template v-if="filter.type === 'single-select'">
                    <Select
                      :options="filter.options"
                      track-by="key"
                      label="label"
                      append-to-body
                      v-model="filter.value"
                      class="flex-1"
                    />
                  </template>

                  <template v-if="filter.type === 'autocomplete'">
                    <Select
                      :options="filter.options"
                      track-by="value"
                      label="label"
                      append-to-body
                      v-model="filter.value"
                      class="flex-1"
                      :filterable="false"
                      :clearable="true"
                      @search="
                        async (e: string, loading: any) => {
                          loading(true);
                          await fetchCustomers(e);
                          loading(false);
                        }
                      "
                      :multiple="true"
                    />
                  </template>

                  <template v-if="filter.type === 'string'">
                    <input
                      type="text"
                      v-model="filter.value"
                      class="flex-1 outline-none border-[1px] border-line rounded-[4px] px-2 py-[7px] focus:outline-none focus:ring focus:ring-primary/40 duration-300 focus:border-primary/40"
                    />
                  </template>
                  <template v-if="filter.type === 'date'">
                    <input
                      type="date"
                      v-model="filter.value"
                      class="flex-1 outline-none border-[1px] border-line rounded-[4px] px-2 min-h-[3rem] py-[3px] focus:outline-none focus:ring focus:ring-primary/40 duration-300 focus:border-primary/40"
                    />
                  </template>
                  <template
                    v-if="
                      filter.type === 'date-range' &&
                      typeof filter.value !== 'string' &&
                      !isArray(filter.value)
                    "
                  >
                    <input
                      type="date"
                      placeholder="From Date"
                      v-model="filter.value.from"
                      class="flex-1 outline-none border-[1px] border-line rounded-[4px] px-2 min-h-[3rem] py-[3px] focus:outline-none focus:ring focus:ring-primary/40 duration-300 focus:border-primary/40"
                    />
                    <input
                      type="date"
                      placeholder="To date"
                      v-model="filter.value.to"
                      class="flex-1 outline-none border-[1px] border-line rounded-[4px] px-2 min-h-[3rem] py-[3px] focus:outline-none focus:ring focus:ring-primary/40 duration-300 focus:border-primary/40"
                    />
                  </template>
                  <button type="button" :title="`Remove ${filter.label}`" @click.stop="removeFilter(filterIndex)">
                    <XMarkIcon class="size-4" />
                  </button>
                </div>
              </template>
              <p v-if="customFilter.length === 0" class="fp-more-hint">Pick a filter below to narrow by status, dates, people, merchants or numbers.</p>
            </div>
          </section>
        </div>

        <footer class="fp-foot">
          <Dropdown
            v-if="computedFilterList.length"
            trigger-style="fp-more-btn"
            :close-on-outside-click="true"
            :close-on-select="true"
          >
            <template #trigger>
              <span class="flex items-center gap-1" data-test="fp-more-filters">
                <PlusIcon class="size-4" aria-hidden="true" />
                More filters
              </span>
            </template>
            <template #contents>
              <ul>
                <li
                  class="px-3 py-2 text-ink cursor-pointer duration-300 hover:bg-surface-2 text-sm"
                  v-for="(filter, filterIndex) in computedFilterList"
                  @click="addFilter(filter)"
                  :key="filterIndex"
                >
                  {{ filter.label }}
                </li>
              </ul>
            </template>
          </Dropdown>
          <span class="flex-1"></span>
          <button type="button" class="fp-show" data-test="fp-show" @click.stop="closePanel">
            <template v-if="counts.total !== null">Show {{ fmt(counts.total) }} {{ counts.total === 1 ? 'order' : 'orders' }}</template>
            <template v-else>Show orders</template>
          </button>
        </footer>
      </div>
    </template>
  </Dropdown>
</template>
<script setup lang="ts">
import { can } from '@/composables';
import { handleResponseException, http } from '@/interceptors/axiosRequestInterceptor';
import { DashboardRequest } from '@/models';
import { useMainStore } from '@/store';
import {
  CheckIcon,
  FunnelIcon,
  PlusIcon,
  XMarkIcon,
} from '@heroicons/vue/24/outline';
import { isArray } from 'lodash';
import { storeToRefs } from 'pinia';
import { computed, nextTick, onMounted, ref, watch } from 'vue';
import { useDebounceFn } from '@vueuse/core';
import Dropdown from '../Shared/Dropdown.vue';
import Select from '../Shared/Select.vue';
import { orderTypes } from '@/utils/constants/orderTypes';
import { SHIPPING_STATUSES } from '@/utils/constants/shippingStatuses';

const CUSTIMOO_STATUSES: Record<string, string> = {
  quote_requested: 'Quote Requested',
  quote_rejected: 'Quote Rejected',
  quote_provided: 'Quote Provided',
  submitted_for_factory_review: 'Submitted for Factory Review',
  order_cancel: 'Cancelled',
  order_approve: 'Mark to Factory',
  factory_approved: 'Factory Approved',
  factory_rejected: 'Factory Rejected',
  submitted_for_customer_review: 'Submitted for Customer Review',
  customer_approved: 'Customer Approved',
  customer_rejected: 'Customer Rejected',
  in_production: 'In Production',
  shipped: 'Shipped',
  completed: 'Completed',
  pending_for_factory_assignment: 'Pending For Factory Assignment',
  production_files_uploaded: 'Production Files Uploaded',
};
/** Matches `order_items.invoice_composite_status` (partial invoicing ledger). */
const ORDER_INVOICE_COMPOSITE_STATUSES: Record<string, string> = {
  not_invoiced: 'Not invoiced',
  partially_invoiced: 'Partially invoiced',
  invoiced: 'Invoiced',
  partially_paid: 'Partially paid',
  paid: 'Paid',
  overdue: 'Overdue',
  uncollectible: 'Uncollectible',
  /** Stripe / old ledger: no automatic collection — matches item invoice status `manual`. */
  manual: 'Manual',
  /** Bug 20222 — sync in flight / awaiting reconciliation. */
  pending_sync: 'Pending sync',
};
type ComparisonOp = 'equal' | 'not_equal';

interface CustomFilter {
  key: string;
  label: string;
  type: 'string' | 'single-select' | 'multi-select' | 'date' | 'autocomplete' | 'date-range';
  value: any;
  comparison?: ComparisonOp;
  options?: { label: string; value: any }[];
}

interface Filter {
  key: string;
  label: string;
  value: string | Array<string> | Record<'to' | 'from', string>;
  isPreset?: boolean;
}

interface Customer {
  label: string;
  value: number | string | Array<string> | Record<'to' | 'from', string>;
}

const emits = defineEmits(['updateFilter']);
const props = defineProps<{
  currentTab: string;
  currentMode: string;
  /** Icon-only trigger with the label in the tooltip (AB#21297). */
  compact?: boolean;
}>();
if (can('view-onhold-orders')) {
  CUSTIMOO_STATUSES['on_hold'] = 'On Hold';
}
const store = useMainStore();
const {
  permissionLoaded,
  orderAdmins,
  csManagers,
  merchantUsers,
  dashboardQuickFilters,
  factories,
  salesRepresentatives,
  shops,
} = storeToRefs(useMainStore());
const currentDashboardModel = localStorage.getItem('dashboardRequest');
const current_filters = ref<DashboardRequest | undefined>(
  currentDashboardModel ? JSON.parse(currentDashboardModel).dashboardRequest : undefined
);
if (current_filters.value && typeof current_filters.value === 'string') {
  current_filters.value = JSON.parse(current_filters.value).dashboardRequest;
}

const selectedAllFilter = ref<CustomFilter[]>([]);
const selectedQuickFilters = ref<string[]>([]);
const selectedUrgentFilters = ref<string[]>([]);
const customFiltersHydrated = ref(false);

function normalizePresetArray(v: unknown): string[] {
  if (v == null || v === '') return [];
  if (Array.isArray(v)) return v.map((x) => String(x));
  return [String(v)];
}

onMounted(async () => {
  const savedQuickFilters = localStorage.getItem('dashboardQuickFilters');
  if (savedQuickFilters) {
    const quickFiltersObj = JSON.parse(savedQuickFilters);
    const quickVals: string[] = [];
    if (quickFiltersObj.filter_by) {
      quickVals.push(...normalizePresetArray(quickFiltersObj.filter_by));
    }
    if (quickFiltersObj.items_for) {
      quickVals.push(...normalizePresetArray(quickFiltersObj.items_for));
    }
    selectedQuickFilters.value = quickVals;
    if (quickFiltersObj.urgent_order) {
      selectedUrgentFilters.value = normalizePresetArray(quickFiltersObj.urgent_order);
    }
    if (Object.keys(quickFiltersObj).length > 0) {
      emits('updateFilter', {
        quick_filters: quickFiltersObj,
      });
    }
  }
  await store.getAllMerchants();
  if (can('filter-shops')) {
    await store.getAllShops();
  }
  watch(
    allFilters,
    (filters) => {
      const savedQuickFilters = localStorage.getItem('dashboardQuickFilters');
      if (filters.length === 0) {
        if (!savedQuickFilters) {
          customFilter.value = [];
          selectedAllFilter.value = [];
          customFiltersHydrated.value = true;
        }
        return;
      }
      if (savedQuickFilters) {
        try {
          const quickFiltersObj = JSON.parse(savedQuickFilters);
          const customKeys = Object.keys(quickFiltersObj).filter(
            (key) => key !== 'filter_by' && key !== 'urgent_order' && key !== 'items_for'
          );
          const restoredFilters = [];
          customKeys.forEach((key) => {
          const filterDef = filters.find((f) => f.key === key);
          if (!filterDef) return;
        
          let savedValue = quickFiltersObj[key]; // { comparison, values }
        
          // For multi-select, map the array of values to option objects
          if (
            filterDef.type === 'multi-select' &&
            savedValue &&
            Array.isArray(savedValue.values)
          ) {
            savedValue = {
              ...savedValue,
              values: savedValue.values.map((val) => {
                if (typeof val === 'object' && val !== null && 'value' in val)
                  return val;
                const opt = filterDef.options?.find((o) => o.value === val);
                return opt || { label: val, value: val };
              }),
            };
          }
        
          // Assign both comparison and values
          const filterClone = { ...filterDef, ...savedValue, value: savedValue.values || [] };
          restoredFilters.push(filterClone);
          });
          customFilter.value = restoredFilters;
          selectedAllFilter.value = restoredFilters.map((f) => ({ ...f }));
        } catch {}
      } else if (!savedQuickFilters) {
        customFilter.value = [];
        selectedAllFilter.value = [];
      }
      customFiltersHydrated.value = true;
    },
    { immediate: true }
  );
});

const filterPresets = ref<Filter[]>([]);
const filterUrgentOrders = ref<Filter[]>([]);
const allFilters = ref<CustomFilter[]>([]);
const customFilter = ref<CustomFilter[]>([]);
const activeFilterCount = computed(
  () => selectedQuickFilters.value.length + selectedUrgentFilters.value.length + customFilter.value.length
);
const customers = ref<Customer[]>([]);

const computedFilterList = computed(() => {
  if (props.currentMode === 'calendar') {
    return allFilters.value.filter(
      (filter) =>
        filter.key === 'customers' ||
        filter.key === 'merchants' ||
        filter.key === 'factory_ids' ||
        filter.key === 'order_admins' ||
        filter.key === 'csmanager' ||
        filter.key === 'sales_representative'
    );
  }
  return allFilters.value.filter((filter) => !customFilter.value.find((f) => f.key === filter.key));
});
function buildQuickFiltersObj(
  customFilterArr: CustomFilter[],
  presetGroups: Record<string, string[]>
) {
  const quickFiltersObj: Record<string, any> = {};

  Object.entries(presetGroups).forEach(([key, values]) => {
    if (values && values.length > 0) {
      quickFiltersObj[key] = values;
    }
  });

  customFilterArr.forEach((f) => {
    if (
      f.value === null ||
      f.value === undefined ||
      f.value === '' ||
      (Array.isArray(f.value) && f.value.length === 0)
    ) {
      return;
    }

    if (
      f.type === 'date-range' &&
      typeof f.value === 'object' &&
      (f.value?.from || f.value?.to)
    ) {
      // Allow a single bound (single date / open-ended) as well as a full range.
      const range: Record<string, string> = {};
      if (f.value.from) range.from = f.value.from;
      if (f.value.to) range.to = f.value.to;
      quickFiltersObj[f.key] = {
        comparison: f.comparison ?? 'equal',
        values: range,
      };
      return;
    }

    // An empty date-range (no from/to set) carries no filter — skip it.
    if (f.type === 'date-range') {
      return;
    }

    if (Array.isArray(f.value)) {
      quickFiltersObj[f.key] = {
        comparison: f.comparison ?? 'equal',
        values: f.value.map((v) =>
          typeof v === 'object' && v !== null && 'value' in v ? v.value : v
        ),
      };
      return;
    }

    quickFiltersObj[f.key] = {
      comparison: f.comparison ?? 'equal',
      values: f.value,
    };
  });

  return quickFiltersObj;
}






function buildPresetGroupsFromSelections(): Record<string, string[]> {
  const groups: Record<string, string[]> = {};
  selectedQuickFilters.value.forEach((val) => {
    const def = filterPresets.value.find((f) => String(f.value) === val);
    if (!def) return;
    if (!groups[def.key]) groups[def.key] = [];
    groups[def.key].push(val);
  });
  const urgentPresets = Array.isArray(filterUrgentOrders.value) ? filterUrgentOrders.value : [];
  selectedUrgentFilters.value.forEach((val) => {
    const def = urgentPresets.find((f) => String(f.value) === val);
    if (!def) return;
    if (!groups[def.key]) groups[def.key] = [];
    groups[def.key].push(val);
  });
  return groups;
}

const getQuickFilterData = (value: string, e: Event) => {
  const el = e.target as HTMLInputElement;
  if (el.checked) {
    if (!selectedQuickFilters.value.includes(value)) {
      selectedQuickFilters.value = [...selectedQuickFilters.value, value];
    }
  } else {
    selectedQuickFilters.value = selectedQuickFilters.value.filter((v) => v !== value);
  }
};

const getUrgentOrderFilters = (value: string, e: Event) => {
  const el = e.target as HTMLInputElement;
  if (el.checked) {
    if (!selectedUrgentFilters.value.includes(value)) {
      selectedUrgentFilters.value = [...selectedUrgentFilters.value, value];
    }
  } else {
    selectedUrgentFilters.value = selectedUrgentFilters.value.filter((v) => v !== value);
  }
};

const clearFilters = async () => {
  selectedQuickFilters.value = [];
  selectedUrgentFilters.value = [];
  dashboardQuickFilters.value = null;
  customFilter.value = [];
  selectedAllFilter.value = [];
  localStorage.removeItem('dashboardQuickFilters');
  emits('updateFilter', {});
};
const addFilter = (filter: CustomFilter): void => {
  moreOpen.value = true;
  const filterClone = JSON.parse(JSON.stringify(filter));
  selectedAllFilter.value = [...selectedAllFilter.value, filterClone];
  customFilter.value = [...customFilter.value, filterClone];
};
const removeFilter = async (index: number): Promise<void> => {
  selectedAllFilter.value = selectedAllFilter.value.filter((_, i) => i !== index);
  customFilter.value = customFilter.value.filter((_, i) => i !== index);
  await nextTick();
};
const getFilterPresets = (): Filter[] => {
  if (props.currentMode === 'calendar') {
    return [
      can('show-my-assign-orders') && {
        label: 'My Assigned Orders',
        value: 'my_assigned_orders',
        key: 'items_for',
        isPreset: true,
      },
    ].filter(Boolean);
  }

  const presets: Filter[] = [
    can('can-resolve-comment') && {
      label: 'Unresolved Comments',
      value: 'unresolved_comments',
      key: 'filter_by',
      isPreset: true,
    },
    {
      label: 'Reorders',
      value: 'reorders',
      key: 'filter_by',
      isPreset: true,
    },
    {
      label: `<p>Awaiting Customer Feedback</p>`,
      value: 'customer_await_items',
      key: 'filter_by',
      isPreset: true,
    },
    can('filter-factory-pi') && {
      label: `<p>Factory PI</p>`,
      value: 'factory_pi',
      key: 'filter_by',
      isPreset: true,
    },
    can('show-order-admin-assigned') && {
      label: `<p>No Order Admin Assigned</p>`,
      value: 'no_order_admin',
      key: 'filter_by',
      isPreset: true,
    },
    can('show-factory-filter') && {
      label: `<p>Awaiting Factory Actions</p>`,
      value: 'pending_items',
      key: 'filter_by',
      isPreset: true,
    },
    can('view-samples') && {
      label: `<p>Samples</p>`,
      value: 'samples',
      key: 'filter_by',
      isPreset: true,
    },
    {
      label: `<p>Quote Requested</p>`,
      value: 'quote_requested_items',
      key: 'filter_by',
      isPreset: true,
    },
    {
      label: `<p>Open</p>`,
      value: 'open_orders',
      key: 'filter_by',
      isPreset: true,
    },
    {
      label: `<p>Shop in shop order</p>`,
      value: 'is_shop_order',
      key: 'filter_by',
    },
    (can('read-dqc-status') || can('write-dqc-status')) && {
      label: `<p>DQC Approved</p>`,
      value: 'marked_dqc',
      key: 'filter_by',
      isPreset: true,
    },
    (can('read-dqc-status') || can('write-dqc-status')) && {
      label: `<p>DQC Not Approved</p>`,
      value: 'not_marked_dqc',
      key: 'filter_by',
      isPreset: true,
    },
  ].filter((item) => typeof item !== 'boolean');

  return presets;
};

const getFilterUrgentOrders = (): Filter[] => {
  return [
    {
      label: `<p class="flex flex-row justify-between items-center gap-x-4"> <span>Extra Attention</span> <span class="rounded-full bg-blue-300 px-[10px] py-[10px]"></span></p>`,
      value: 'extra_attention',
      key: 'urgent_order',
      isPreset: true,
    },
    {
      label: `<p class="flex flex-row justify-between items-center gap-x-4"> <span>Urgent</span> <span class="rounded-full order_cancel px-[10px] py-[10px]"></span></p>`,
      value: 'urgent',
      key: 'urgent_order',
      isPreset: true,
    },
    {
      label: `<p class="flex flex-row justify-between items-center gap-x-4"> <span>VIP</span> <span class="rounded-full bg-green-300 px-[10px] py-[10px]"></span></p>`,
      value: 'vip',
      key: 'urgent_order',
      isPreset: true,
    },
  ].filter((item) => typeof item !== 'boolean');
};

const getAllFilters = (): CustomFilter[] => {
  const filters: CustomFilter[] = [
    {
      key: 'status',
      label: 'Status',
      type: 'multi-select',
      value: [],
      comparison: 'equal' as ComparisonOp,
      options: Object.keys(CUSTIMOO_STATUSES).map((key) => ({
        label: CUSTIMOO_STATUSES[key],
        value: key,
      })),
    },
    ...(can('view-payment-status')
      ? [
          {
            key: 'invoice_composite_status',
            label: 'Invoice Status',
            type: 'multi-select' as const,
            value: [],
            comparison: 'equal' as ComparisonOp,
            options: Object.keys(ORDER_INVOICE_COMPOSITE_STATUSES).map((key) => ({
              label: ORDER_INVOICE_COMPOSITE_STATUSES[key],
              value: key,
            })),
          },
        ]
      : []),
    {
      key: 'shipping_status',
      label: 'Shipping Status',
      type: 'multi-select',
      value: [],
      comparison: 'equal' as ComparisonOp,
      options: Object.keys(SHIPPING_STATUSES).map((key) => ({
        label: SHIPPING_STATUSES[key],
        value: key,
      })),
    },
    {
      key: 'created_at',
      label: 'Order Creation',
      type: 'date-range',
      value: {
        to: '',
        from: '',
      },
      comparison: 'equal' as ComparisonOp,
    },
    {
      key: 'est_shipping_date',
      label: 'Estimated Shipping Date',
      type: 'date-range',
      value: {
        to: '',
        from: '',
      },
      comparison: 'equal' as ComparisonOp,
    },
    ...(can('order_administrator_list')
      ? [
          {
            key: 'order_admins',
            label: 'Order Admins',
            type: 'multi-select' as const,
            value: [],
            comparison: 'equal' as ComparisonOp, 
            options: orderAdmins.value.map((admin) => ({
              label: admin.name,
              value: admin.id,
            })),
          },
        ]
    : []),
    ...(can('order_administrator_list')
      ? [
          {
            key: 'secondary_order_admins',
            label: 'Secondary Admins',
            type: 'multi-select' as const,
            value: [],
            comparison: 'equal' as ComparisonOp, 
            options: orderAdmins.value.map((admin) => ({
              label: admin.name,
              value: admin.id,
            })), 
          },
        ]
    : []),
    ...(can('order_csmanager_list')
      ? [
          {
            key: 'csmanager',
            label: 'Customer Success Manager',
            type: 'multi-select' as const,
          value: [],
          comparison: 'equal' as ComparisonOp, 
            options: csManagers.value.map((admin) => ({
              label: admin.name,
              value: admin.id,
            })),
          },
        ]
      : []),
    ...(can('order_sales_representative_list')
      ? [
          {
            key: 'sales_representative',
            label: 'Sales Representative',
            type: 'multi-select' as const,
          value: [],
         comparison: 'equal' as ComparisonOp, 
            options: salesRepresentatives.value.map((admin) => ({
              label: admin.name,
              value: admin.id,
            })),
          },
        ]
      : []),
    {
      key: 'customers',
      label: 'Customer',
      type: 'autocomplete',
      value: [],
      comparison: 'equal' as ComparisonOp, 
      options: customers.value.map((customer) => ({
        label: customer.label,
        value: customer.value,
      })),
    },
    ...(can('merchants_list')
      ? [
          {
            key: 'merchants',
            label: 'Merchant',
            type: 'multi-select' as const,
            value: [],
           comparison: 'equal' as ComparisonOp, 
            options: merchantUsers.value.map((merchant) => ({
              label: merchant.company_name,
              value: merchant.id,
            })),
          },
        ]
      : []),
    // filter for shops
    ...(can('filter-shops')
      ? [
          {
            key: 'shop',
            label: 'Shop',
            type: 'multi-select' as const,
            value: '',
            comparison: 'equal' as ComparisonOp,
            options: shops.value.map((shop) => ({
              label: shop.name,
              value: shop.id,
            })),
          },
        ]
      : []),
    ...(can('order-types-filter')
      ? [
          {
            key: 'order_types',
            label: 'Order Types',
            type: 'multi-select' as const,
            value: [],
           comparison: 'equal' as ComparisonOp, 
            options: orderTypes.map((type) => ({
              label: type.label,
              value: type.symbol,
            })),
          },
        ]
      : []),
  ];

  if (
    props.currentMode === 'calendar' &&
    factories.value.length > 0 &&
    can('show-factory-filter')
  ) {
    filters.push({
      key: 'factory_ids',
      label: 'Factory',
      type: 'multi-select',
      value: [],
     comparison: 'equal' as ComparisonOp, 
      options: factories.value.map((factory) => ({
        label: factory.name,
        value: factory.id,
      })),
    });
  }
  if(can('view-po-filter')) {
    filters.push({
      key: 'po_number',
      label: 'PO',
      type: 'string',
      value: '',
    });
  }

  filters.push({
    key: 'invoice_number',
    label: 'Invoice Number',
    type: 'string',
    value: '',
    comparison: 'equal' as ComparisonOp,
  });

  if (
    props.currentMode != 'calendar' &&
    factories.value.length > 0 &&
    can('show-factory-filter-table-view')
  ) {
    filters.push({
      key: 'factory_filter_ids',
      label: 'Factory',
      type: 'multi-select',
      value: [],
      options: factories.value.map((factory) => ({
        label: factory.name,
        value: factory.id,
      })),
    });
  }

  return filters;
};

watch(
  [
    () => permissionLoaded.value,
    () => props.currentTab,
    () => merchantUsers.value,
    () => orderAdmins.value,
    () => csManagers.value,
    () => salesRepresentatives.value,
    () => props.currentMode,
  ],
  (newVal) => {
    if (newVal) {
      filterPresets.value = getFilterPresets();
      filterUrgentOrders.value = can('show-urgency-filters') ? getFilterUrgentOrders() : [];
      if (
        current_filters.value &&
        filterPresets.value.some(
          (filter: Filter) =>
            filter.value === (current_filters.value ? current_filters.value.items_for : '')
        )
      ) {
        let skipTabPresetSync = false;
        try {
          const raw = localStorage.getItem('dashboardQuickFilters');
          if (raw) {
            const o = JSON.parse(raw);
            if (
              normalizePresetArray(o.filter_by).length > 0 ||
              normalizePresetArray(o.items_for).length > 0
            ) {
              skipTabPresetSync = true;
            }
          }
        } catch {
          /* ignore */
        }
        if (!skipTabPresetSync) {
          selectedQuickFilters.value = [String(current_filters.value.items_for)];
        }
      }
      allFilters.value = getAllFilters();
    }
  },
  // Immediate as well (AB#21490): when the store already has permissions and
  // people by the time this mounts — a second visit to the dashboard — nothing
  // in the watched list changes, and the panel would open with no chips.
  { deep: true, immediate: true }
);

const fetchCustomers = async (query: string): Promise<boolean> => {
  if (!query || query.length < 3) {
    customers.value = [];
    return true;
  }

  try {
    const resp = await http.post(`/search-customers`, { query });
    const autoCompleteFilter = customFilter.value.find((filter) => filter.key === 'customers');
    if (autoCompleteFilter) {
      autoCompleteFilter.options = [
        ...resp.data.result.map((customer: any) => ({
          label: `${customer.full_name}`,
          value: `${customer.email}`,
        })),
      ];
    }
    return true;
  } catch (e: any) {
    handleResponseException(e);
    return true;
  }
};

/* ------------------------------------------------------------------ */
/* AB#21490 — the panel: groups, labels, applied chips, counts, close. */
/* ------------------------------------------------------------------ */

const panel = ref<{ close: (returnFocus?: boolean) => void } | null>(null);
const moreOpen = ref(false);
const panelOpen = ref(false);

/** Plain labels for the chips (the presets carry HTML labels for the old UI). */
const CHIP_LABELS: Record<string, string> = {
  unresolved_comments: 'Unresolved comments',
  customer_await_items: 'Awaiting customer feedback',
  pending_items: 'Awaiting factory actions',
  no_order_admin: 'No order admin assigned',
  reorders: 'Reorders',
  samples: 'Samples',
  is_shop_order: 'Shop-in-shop',
  quote_requested_items: 'Quote requested',
  open_orders: 'Open',
  factory_pi: 'Factory PI',
  marked_dqc: 'DQC approved',
  not_marked_dqc: 'DQC not approved',
  my_assigned_orders: 'My assigned orders',
  extra_attention: 'Extra attention',
  urgent: 'Urgent',
  vip: 'VIP',
};

/** Which group a preset sits in. Anything unlisted lands in "Other" rather than vanishing. */
const GROUP_OF: Record<string, string> = {
  unresolved_comments: 'attention',
  customer_await_items: 'attention',
  pending_items: 'attention',
  no_order_admin: 'attention',
  reorders: 'type',
  samples: 'type',
  is_shop_order: 'type',
  quote_requested_items: 'type',
  open_orders: 'stage',
  factory_pi: 'stage',
  marked_dqc: 'stage',
  not_marked_dqc: 'stage',
};
const GROUP_TITLES: Record<string, string> = {
  attention: 'Needs attention',
  type: 'Order type',
  stage: 'Stage & quality',
  flags: 'Flags',
  other: 'Quick filters',
};
const GROUP_ORDER = ['attention', 'type', 'stage', 'flags', 'other'];
/** Chip order inside a group, as the mockup reads; unlisted chips keep their preset order after these. */
const CHIP_ORDER = Object.keys(GROUP_OF);
const chipRank = (value: string): number => {
  const i = CHIP_ORDER.indexOf(value);
  return i === -1 ? CHIP_ORDER.length : i;
};
const FLAG_DOT: Record<string, string> = { extra_attention: 'blue', urgent: 'red', vip: 'green' };

const plainLabel = (f: Filter): string =>
  CHIP_LABELS[String(f.value)] ?? String(f.label).replace(/<[^>]+>/g, '').trim();

interface Chip {
  value: string;
  label: string;
  selected: boolean;
  count: number | null;
  dot?: string;
  toggle: () => void;
}

const toggleQuick = (value: string): void => {
  selectedQuickFilters.value = selectedQuickFilters.value.includes(value)
    ? selectedQuickFilters.value.filter((v) => v !== value)
    : [...selectedQuickFilters.value, value];
};
const toggleFlag = (value: string): void => {
  selectedUrgentFilters.value = selectedUrgentFilters.value.includes(value)
    ? selectedUrgentFilters.value.filter((v) => v !== value)
    : [...selectedUrgentFilters.value, value];
};

const groups = computed(() => {
  const byGroup = new Map<string, Chip[]>();
  const put = (g: string, chip: Chip) => {
    if (!byGroup.has(g)) byGroup.set(g, []);
    byGroup.get(g)!.push(chip);
  };

  for (const f of filterPresets.value) {
    const value = String(f.value);
    put(GROUP_OF[value] ?? 'other', {
      value,
      label: plainLabel(f),
      selected: selectedQuickFilters.value.includes(value),
      count: counts.value.chips[value] ?? null,
      toggle: () => toggleQuick(value),
    });
  }

  if (props.currentMode !== 'calendar') {
    for (const f of filterUrgentOrders.value) {
      const value = String(f.value);
      put('flags', {
        value,
        label: plainLabel(f),
        selected: selectedUrgentFilters.value.includes(value),
        count: counts.value.flags[value] ?? null,
        dot: FLAG_DOT[value],
        toggle: () => toggleFlag(value),
      });
    }
  }

  return GROUP_ORDER.filter((g) => byGroup.has(g)).map((g) => ({
    id: g,
    title: GROUP_TITLES[g],
    chips: [...byGroup.get(g)!].sort((a, b) => chipRank(a.value) - chipRank(b.value)),
  }));
});

interface AppliedChip {
  id: string;
  label: string;
  tone: 'quick' | 'flag' | 'custom';
  dot?: string;
  remove: () => void;
}

/** The custom filter's value in a few words, for its applied chip. */
const customSummary = (f: CustomFilter): string => {
  const v = f.value;
  const not = f.comparison === 'not_equal' ? 'not ' : '';
  if (v == null || v === '') return f.label;
  if (Array.isArray(v)) {
    const names = v.map((x) => (typeof x === 'object' && x !== null ? String(x.label ?? x.value) : String(x)));
    if (names.length === 0) return f.label;
    return `${f.label}: ${not}${names.length > 2 ? `${names.slice(0, 2).join(', ')} +${names.length - 2}` : names.join(', ')}`;
  }
  if (typeof v === 'object') {
    const from = (v as { from?: string }).from;
    const to = (v as { to?: string }).to;
    if (!from && !to) return f.label;
    return `${f.label}: ${from || '…'} → ${to || '…'}`;
  }
  return `${f.label}: ${not}${String(v)}`;
};

const appliedChips = computed<AppliedChip[]>(() => {
  const out: AppliedChip[] = [];
  for (const value of selectedQuickFilters.value) {
    const def = filterPresets.value.find((f) => String(f.value) === value);
    out.push({ id: value, label: def ? plainLabel(def) : value, tone: 'quick', remove: () => toggleQuick(value) });
  }
  for (const value of selectedUrgentFilters.value) {
    const def = filterUrgentOrders.value.find((f) => String(f.value) === value);
    out.push({ id: value, label: def ? plainLabel(def) : value, tone: 'flag', dot: FLAG_DOT[value], remove: () => toggleFlag(value) });
  }
  customFilter.value.forEach((f, i) => {
    out.push({ id: `custom-${f.key}`, label: customSummary(f), tone: 'custom', remove: () => removeFilter(i) });
  });
  return out;
});

/* ---- counts ---- */

interface Counts {
  total: number | null;
  base: number | null;
  chips: Record<string, number>;
  flags: Record<string, number>;
}

const counts = ref<Counts>({ total: null, base: null, chips: {}, flags: {} });
const countsLoading = ref(false);
let countsSeq = 0;

const fmt = (n: number): string => n.toLocaleString('en-GB');

/**
 * One request, one number per chip, computed by the server within the
 * current tab and everything else applied (AB#21490). Sent through the
 * dashboard route with `counts_for`, with the same query string and body
 * the table uses, so a chip never disagrees with the table.
 */
const fetchCounts = async (): Promise<void> => {
  if (props.currentMode === 'calendar') return;
  const seq = ++countsSeq;
  countsLoading.value = true;

  try {
    const request = { ...(store.dashboardRequest as Record<string, unknown>), page: 1, per_page: 1, items_for: props.currentTab || 'all_items' };
    const query = new URLSearchParams(
      Object.fromEntries(Object.entries(request).filter(([, v]) => v !== undefined && v !== null).map(([k, v]) => [k, String(v)]))
    ).toString();
    const quick = buildQuickFiltersObj(customFilter.value, buildPresetGroupsFromSelections());
    const { data } = await http.post(`dashboard?${query}`, {
      quick_filters: quick,
      counts_for: filterPresets.value.map((f) => String(f.value)),
      counts_for_flags: filterUrgentOrders.value.map((f) => String(f.value)),
    });

    if (seq !== countsSeq) return;

    const r = (data?.result ?? {}) as Partial<Counts> & { counts?: Record<string, number> };
    counts.value = {
      total: typeof r.total === 'number' ? r.total : null,
      base: typeof r.base === 'number' ? r.base : null,
      chips: r.counts ?? {},
      flags: r.flags ?? {},
    };
  } catch {
    // Counts are a convenience; the chips still work without them.
    if (seq === countsSeq) counts.value = { total: null, base: null, chips: {}, flags: {} };
  } finally {
    if (seq === countsSeq) countsLoading.value = false;
  }
};

const fetchCountsSoon = useDebounceFn(fetchCounts, 250);

const onPanelOpen = (open: boolean): void => {
  panelOpen.value = open;
  if (open) fetchCounts();
};

watch([selectedQuickFilters, selectedUrgentFilters, customFilter], () => {
  if (panelOpen.value) fetchCountsSoon();
}, { deep: true });

const closePanel = (): void => {
  panel.value?.close(true);
};

watch(
  [customFilter, selectedQuickFilters, selectedUrgentFilters, filterPresets, filterUrgentOrders],
  ([newFilters]) => {
    const urgentList = Array.isArray(filterUrgentOrders.value) ? filterUrgentOrders.value : [];
    if (selectedQuickFilters.value.length > 0 && filterPresets.value.length === 0) {
      return;
    }
    if (selectedUrgentFilters.value.length > 0 && urgentList.length === 0) {
      return;
    }
    if (!customFiltersHydrated.value && (newFilters as CustomFilter[]).length === 0) {
      return;
    }
    const presetGroups = buildPresetGroupsFromSelections();
    const quickFiltersObj = buildQuickFiltersObj(newFilters as CustomFilter[], presetGroups);
    localStorage.setItem('dashboardQuickFilters', JSON.stringify(quickFiltersObj));
    emits('updateFilter', {
      quick_filters: quickFiltersObj,
    });
  },
  { deep: true }
);
</script>
<style lang="scss" scoped>
/* AB#21490 — the Filters panel. px on purpose: the root font is 12.8px. */
.fp {
  width: min(92vw, 760px);
  display: flex;
  flex-direction: column;
  border-radius: 12px;
  overflow: hidden;
  color: var(--ink);
}
.fp-head {
  display: flex;
  align-items: baseline;
  gap: 12px;
  padding: 14px 18px 12px;
}
.fp-title {
  font-size: 16px;
  font-weight: 700;
  margin: 0;
}
.fp-count {
  font-size: 12px;
  color: var(--ink-soft);
  font-variant-numeric: tabular-nums;
  flex: 1;
}
.fp-clear {
  background: none;
  border: 0;
  color: var(--ink-soft);
  font-size: 12.5px;
  cursor: pointer;
}
.fp-clear:hover {
  color: var(--danger);
}
.fp-applied {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  padding: 10px 18px;
  background: var(--surface-2);
  border-top: 1px solid var(--border-soft);
  border-bottom: 1px solid var(--border-soft);
}
.fp-applied-lbl {
  font-size: 10.5px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--ink-soft);
  margin-right: 4px;
}
.fp-achip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 28px;
  padding: 0 8px 0 10px;
  border-radius: 20px;
  border: 1px solid var(--accent-soft-2);
  background: var(--accent-soft);
  color: var(--accent);
  font-size: 12.5px;
  cursor: pointer;
}
.fp-achip--flag {
  border-color: var(--danger);
  background: var(--danger-soft);
  color: var(--danger);
}
.fp-achip--custom {
  border-color: var(--border);
  background: var(--surface);
  color: var(--ink);
}
.fp-achip-x {
  width: 13px;
  height: 13px;
}
.fp-body {
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 14px 18px 4px;
  max-height: 60vh;
  overflow-y: auto;
}
.fp-group-t {
  margin: 0 0 8px;
  font-size: 10.5px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--ink-soft);
}
.fp-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.fp-chip {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  height: 34px;
  padding: 0 12px;
  border-radius: 20px;
  border: 1px solid var(--border);
  background: var(--surface);
  color: var(--ink);
  font-size: 13px;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;
  user-select: none;
}
.fp-chip:hover {
  background: var(--surface-2);
}
.fp-chip--on {
  background: var(--accent-soft);
  border-color: var(--accent);
  color: var(--accent);
  font-weight: 600;
}
.fp-chip--on.fp-chip--red {
  background: var(--danger-soft);
  border-color: var(--danger);
  color: var(--danger);
}
.fp-chip--zero {
  color: var(--ink-mute);
}
.fp-chip-check {
  width: 14px;
  height: 14px;
}
.fp-chip-n {
  font-size: 11.5px;
  color: var(--ink-soft);
  font-variant-numeric: tabular-nums;
}
.fp-chip--on .fp-chip-n {
  color: inherit;
}
.fp-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  display: inline-block;
}
.fp-dot--blue {
  background: #7cb0f0;
}
.fp-dot--red {
  background: #e08c86;
}
.fp-dot--green {
  background: #7fd1a8;
}
.fp-more-hint {
  margin: 0;
  font-size: 12px;
  color: var(--ink-soft);
}
.fp-foot {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 18px 14px;
  border-top: 1px solid var(--border-soft);
  margin-top: 8px;
}
:deep(.fp-more-btn) {
  background: none;
  border: 0;
  color: var(--ink);
  font-size: 13px;
  cursor: pointer;
  padding: 4px 0;
}
.fp-show {
  height: 40px;
  padding: 0 20px;
  border-radius: 8px;
  border: 0;
  background: var(--accent);
  color: #fff;
  font-weight: 600;
  font-size: 13.5px;
  cursor: pointer;
}
.fp-show:hover {
  background: var(--accent-strong);
}
@media (max-width: 640px) {
  .fp-body {
    max-height: 55vh;
  }
  .fp-chip {
    height: 32px;
    font-size: 12.5px;
  }
}
</style>
